import { useEffect, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  Badge, Box, Button, Center, Flex, Spinner, Text, useColorModeValue, useDisclosure,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import Card from 'components/card/Card';
import * as EmployeeService from 'services/EmployeeService';
import * as InterventionReportService from 'services/InterventionReportService';
import { Employee, InterventionReportResponse, InterventionReportStatus, UpdateInterventionReportRequest } from 'utils/interfaces';
import { isAdmin as checkIsAdmin } from 'utils/auth-utils';
import { toHHmm } from 'utils/common-utils';
import InterventionReportForm, { InterventionReportFormHandle } from './components/InterventionReportForm';
import InterventionReportPrintView from './components/InterventionReportPrintView';
import ReportSidebar from './components/sidebar/ReportSidebar';
import { TimelineEvent } from './components/sidebar/ReportStatusTimeline';
import { ReportInfoItem } from './components/sidebar/ReportInformation';
import { generateReportPdf } from './pdf/generateReportPdf';
import { generateAttachmentsPdf } from './pdf/generateAttachmentsPdf';
import { displayableClientEmail, displayReportDate, ReportAttachment, ReportFormState } from './types';

interface RouteParams {
  id: string;
}

const STATUS_LABELS: Record<InterventionReportStatus, string> = {
  SUBMITTED: 'Compilato',
  EDITED: 'Modificato',
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return `${format(date, 'dd/MM/yyyy')} alle ${format(date, 'HH:mm')}`;
};

// Data-driven timeline built from the full edit history the backend returns (one row per
// create/edit, not just the latest one) — no "in revisione"/"approvato" placeholder stages,
// since that workflow doesn't exist on the backend today.
const buildTimelineEvents = (report: InterventionReportResponse): TimelineEvent[] => {
  // Reports created before the history table existed have no CREATED row (only EDITED rows for
  // any edit made since) — synthesize the missing "Compilato" from fields that have always been
  // there, so it doesn't silently disappear once such a report gets its first tracked edit.
  const hasCreatedEntry = report.history.some(entry => entry.action === 'CREATED');
  const compiledEvent = {
    label: 'Compilato',
    actor: `${report.compiledByEmployee.firstName} ${report.compiledByEmployee.lastName}`,
    timestamp: formatDateTime(report.submittedAt),
  };

  const events = [
    ...(hasCreatedEntry ? [] : [compiledEvent]),
    ...report.history.map(entry => ({
      label: entry.action === 'CREATED' ? 'Compilato' : 'Modificato',
      actor: [entry.firstName, entry.lastName].filter(Boolean).join(' ') || 'Amministratore',
      timestamp: formatDateTime(entry.timestamp),
    })),
  ];

  return events.map((event, idx) => ({
    ...event,
    status: idx === events.length - 1 ? 'current' : 'completed',
  }));
};

const buildInfoItems = (report: InterventionReportResponse): ReportInfoItem[] => [
  { label: 'ID Rapporto', value: report.reportNumberFormatted },
  { label: 'Intervento', value: report.task.title },
  { label: 'Cliente', value: report.customer.fullName },
  { label: 'Data creazione', value: formatDateTime(report.createdAt) },
  { label: 'Ultima modifica', value: formatDateTime(report.updatedAt) },
];

const toFormState = (report: InterventionReportResponse, attachments: ReportAttachment[]): ReportFormState => ({
  reportDate: report.reportDate,
  clientName: report.clientName || '',
  clientCity: report.clientCity || '',
  clientAddress: report.clientAddress || '',
  clientTaxCode: report.clientTaxCode || '',
  clientVatNumber: report.clientVatNumber || '',
  clientPhone: report.clientPhone || '',
  clientEmail: displayableClientEmail(report.clientEmail),
  prestazione: report.prestazione,
  notes: report.notes || '',
  startTime: toHHmm(report.startTime),
  endTime: toHHmm(report.endTime),
  operators: report.operators.map(o => ({ employeeId: o.employeeId, firstName: o.firstName, lastName: o.lastName, present: o.present })),
  attendees: report.attendees,
  vehicles: report.vehicles.map(v => ({ id: v.id, name: v.name, plate: v.plate, selected: true })),
  customerSignerName: report.customerSignerName || '',
  customerSignatureDataUrl: report.customerSignature,
  serviceRating: report.serviceRating,
  attachments,
});

export default function ReportDetailPage() {
  const { id } = useParams<RouteParams>();
  const history = useHistory();
  const { keycloak } = useKeycloak();
  const token = keycloak?.token;
  const admin = checkIsAdmin(keycloak!);
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
  );

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<InterventionReportResponse | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingAttachments, setIsDownloadingAttachments] = useState(false);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [attachmentPreviews, setAttachmentPreviews] = useState<ReportAttachment[]>([]);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteCancelRef = useRef(null);
  const formRef = useRef<InterventionReportFormHandle>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const onSessionExpired = () => keycloak?.logout();

  // AdminLayout resets its own scroll container on route change, but that fires before this page's
  // data has loaded — the loading spinner is short, so there's nothing yet to actually scroll. Once
  // the real (much taller) report content mounts, nothing re-triggers that reset. scrollIntoView
  // (rather than hardcoding which ancestor scrolls) finds whichever container is actually scrollable.
  useEffect(() => {
    topRef.current?.scrollIntoView({ block: 'start' });
  }, [id, loading]);

  const fetchReport = async () => {
    if (!token) return;
    setLoading(true);
    const data = await InterventionReportService.getById(token, onSessionExpired, Number(id));
    if (data) setReport(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id]);

  useEffect(() => {
    if (!token || !admin) return;
    EmployeeService.getAllPaginated(token, onSessionExpired, 0, 100).then(data => {
      if (data) setAllEmployees(data.content);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, admin]);

  // Runs whenever the report loads/reloads (not just in edit mode): the "Allegati" gallery in
  // InterventionReportPrintView needs these blob URLs to display/open attachments in plain view too,
  // and an edit-save needs them to re-embed existing photos into the regenerated PDF (see handleUpdate).
  useEffect(() => {
    if (!token || !report || report.attachments.length === 0) {
      setAttachmentPreviews([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      report.attachments.map(async (att): Promise<ReportAttachment | null> => {
        const blob = await InterventionReportService.downloadAttachment(token, onSessionExpired, report.id, att.id);
        return blob ? { id: att.id, fileName: att.fileName, previewUrl: URL.createObjectURL(blob) } : null;
      })
    ).then(results => {
      if (!cancelled) setAttachmentPreviews(results.filter((r): r is ReportAttachment => r !== null));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, report?.id]);

  const handleDownloadPdf = async () => {
    if (!token || !report) return;
    setIsDownloading(true);
    const blob = await InterventionReportService.downloadPdf(token, onSessionExpired, report.id);
    setIsDownloading(false);
    if (!blob) {
      toast.error('Errore durante il download del PDF');
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `rapporto-intervento-${report.reportNumberFormatted}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Unlike the report PDF (generated once, stored on S3, just fetched here), the attachments PDF is
  // assembled on the fly from the already-fetched attachmentPreviews blob URLs — there's nothing to
  // store server-side for it, so there's no backend endpoint to call.
  const handleDownloadAttachments = async () => {
    if (!report) return;
    setIsDownloadingAttachments(true);
    try {
      const blob = await generateAttachmentsPdf(attachmentPreviews);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `allegati-rapporto-${report.reportNumberFormatted}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Errore durante la generazione del PDF degli allegati');
    } finally {
      setIsDownloadingAttachments(false);
    }
  };

  const handleUpdate = async (formData: ReportFormState, customerSignatureDataUrl: string | null) => {
    if (!token || !report) return;
    setIsSubmitting(true);

    const payload: UpdateInterventionReportRequest = {
      reportDate: formData.reportDate || undefined,
      clientName: formData.clientName,
      clientCity: formData.clientCity,
      clientAddress: formData.clientAddress,
      clientTaxCode: formData.clientTaxCode || undefined,
      clientVatNumber: formData.clientVatNumber || undefined,
      clientPhone: formData.clientPhone || undefined,
      clientEmail: formData.clientEmail || undefined,
      prestazione: formData.prestazione,
      notes: formData.notes || undefined,
      startTime: formData.startTime || undefined,
      endTime: formData.endTime || undefined,
      operators: formData.operators.map(o => ({ employeeId: o.employeeId, present: o.present })),
      attendees: formData.attendees,
      vehicleIds: formData.vehicles.filter(v => v.selected).map(v => v.id),
      customerSignerName: formData.customerSignerName || undefined,
      customerSignatureDataUrl: customerSignatureDataUrl || undefined,
      serviceRating: formData.serviceRating,
    };

    try {
      const pdfBlob = await generateReportPdf({
        reportNumberFormatted: report.reportNumberFormatted,
        reportDate: displayReportDate(report.reportDate),
        taskTitle: report.task.title,
        compiledByLabel: `${report.compiledByEmployee.firstName} ${report.compiledByEmployee.lastName}`,
        employeeSignatureUrl: report.employeeSignatureSnapshot,
        data: { ...formData, customerSignatureDataUrl },
      });

      const newAttachmentFiles = formData.attachments.map(a => a.file).filter((f): f is File => Boolean(f));
      const result = await InterventionReportService.updateReport(token, onSessionExpired, report.id, payload, pdfBlob, newAttachmentFiles);

      switch (result.status) {
        case 'success':
          toast.success('Rapporto aggiornato con successo');
          setReport(result.report);
          setIsEditing(false);
          break;
        case 'missing_signature':
          toast.error('Firma mancante: impossibile salvare il rapporto');
          break;
        case 'conflict':
          toast.error('Conflitto durante il salvataggio del rapporto');
          break;
        case 'forbidden':
          toast.error('Non hai i permessi per modificare questo rapporto');
          break;
        default:
          toast.error('Errore durante il salvataggio del rapporto');
      }
    } catch (err) {
      toast.error('Errore durante la generazione del PDF');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!token || !report) return;
    setIsDeleting(true);
    const success = await InterventionReportService.deleteReport(token, onSessionExpired, report.id);
    setIsDeleting(false);
    if (success) {
      toast.success('Rapporto eliminato con successo');
      onDeleteClose();
      history.push('/admin/piano-settimanale');
    } else {
      toast.error('Errore durante l\'eliminazione del rapporto');
    }
  };

  if (loading) {
    return (
      <Box ref={topRef} pt={{ base: '60px', md: '80px', xl: '78px' }}>
        <Center minH="60vh"><Spinner size="lg" /></Center>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box ref={topRef} pt={{ base: '60px', md: '80px', xl: '78px' }}>
        <Center minH="60vh"><Text color="red.500">Rapporto d'intervento non trovato.</Text></Center>
      </Box>
    );
  }

  const printView = (
    <InterventionReportPrintView
      reportNumberFormatted={report.reportNumberFormatted}
      reportDate={displayReportDate(report.reportDate)}
      taskTitle={report.task.title}
      compiledByLabel={`${report.compiledByEmployee.firstName} ${report.compiledByEmployee.lastName}`}
      employeeSignatureUrl={report.employeeSignatureSnapshot}
      data={toFormState(report, attachmentPreviews)}
    />
  );

  return (
    <Box ref={topRef} pt={{ base: '40px', md: '80px', xl: '78px' }} pb="40px" px={{ base: 2, md: 4 }} mx={{ base: '-20px', md: 0 }}>
      {isEditing ? (
        <>
          <Flex justifyContent="space-between" alignItems="center" mb="20px" flexWrap="wrap" gap="10px">
            <Text fontSize="2xl" fontWeight="bold" color="#9FB50C">
              Rapporto d'Intervento N. {report.reportNumberFormatted}
            </Text>
            <Flex gap="10px">
              <Button variant="ghost" onClick={() => setIsEditing(false)} isDisabled={isSubmitting} height="30px" fontSize="sm">
                Annulla
              </Button>
              <Button
                onClick={() => formRef.current?.submit()}
                isLoading={isSubmitting}
                _hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.600' }} _focus={{ boxShadow: 'none' }}
                backgroundColor="#688CC8" color="white" height="30px" borderRadius="6px" fontSize="sm"
              >
                Salva modifiche
              </Button>
              {admin && (
                <Button onClick={onDeleteOpen} colorScheme="red" height="30px" borderRadius="6px" fontSize="sm">
                  Elimina
                </Button>
              )}
            </Flex>
          </Flex>
          <InterventionReportForm
            ref={formRef}
            reportNumberFormatted={report.reportNumberFormatted}
            reportDate={displayReportDate(report.reportDate)}
            taskTitle={report.task.title}
            compiledByLabel={`${report.compiledByEmployee.firstName} ${report.compiledByEmployee.lastName}`}
            employeeSignatureUrl={report.employeeSignatureSnapshot}
            allEmployees={allEmployees}
            initialData={toFormState(report, attachmentPreviews)}
            onSubmit={handleUpdate}
            isSubmitting={isSubmitting}
            submitLabel="Salva modifiche"
            onCancel={() => setIsEditing(false)}
            readOnlyCustomerSignature
            canEditReportDate={admin}
            hideActions
          />
        </>
      ) : admin ? (
        <>
          <Box mb="20px">
            <Flex align="center" gap={3} flexWrap="wrap">
              <Text fontSize="2xl" fontWeight="bold" color="#9FB50C">
                Rapporto d'Intervento N. {report.reportNumberFormatted}
              </Text>
              <Badge colorScheme={report.status === 'EDITED' ? 'blue' : 'green'}>{STATUS_LABELS[report.status]}</Badge>
            </Flex>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Compilato da {report.compiledByEmployee.firstName} {report.compiledByEmployee.lastName} il {formatDateTime(report.submittedAt)}
            </Text>
          </Box>

          <Flex direction={{ base: 'column', lg: 'row' }} align="flex-start" gap={5}>
            <Card flex="1" minW={0} boxShadow={shadow} bg="gray.100" p={{ base: '10px', md: '24px' }}>
              {printView}
            </Card>
            <ReportSidebar
              timelineEvents={buildTimelineEvents(report)}
              canManage={admin}
              onEdit={() => setIsEditing(true)}
              onDownload={handleDownloadPdf}
              isDownloading={isDownloading}
              onDownloadAttachments={handleDownloadAttachments}
              isDownloadingAttachments={isDownloadingAttachments}
              hasAttachments={report.attachments.length > 0}
              onDelete={onDeleteOpen}
              infoItems={buildInfoItems(report)}
            />
          </Flex>
        </>
      ) : (
        <>
          <Flex justifyContent="space-between" alignItems="center" mb="20px" flexWrap="wrap" gap="10px">
            <Text fontSize="2xl" fontWeight="bold" color="#9FB50C">
              Rapporto d'Intervento N. {report.reportNumberFormatted}
            </Text>
            <Flex gap="10px">
              <Button
                onClick={handleDownloadPdf}
                isLoading={isDownloading}
                _hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.600' }} _focus={{ boxShadow: 'none' }}
                backgroundColor="#688CC8" color="white" height="30px" borderRadius="6px" fontSize="sm"
              >
                Scarica PDF
              </Button>
              {report.attachments.length > 0 && (
                <Button
                  onClick={handleDownloadAttachments}
                  isLoading={isDownloadingAttachments}
                  variant="outline"
                  borderColor="gray.300"
                  _focus={{ boxShadow: 'none' }}
                  height="30px" borderRadius="6px" fontSize="sm"
                >
                  Scarica allegati
                </Button>
              )}
            </Flex>
          </Flex>
          <Card boxShadow={shadow} bg="gray.100" p={{ base: '10px', md: '24px' }}>
            {printView}
          </Card>
        </>
      )}

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={deleteCancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Elimina rapporto d'intervento</AlertDialogHeader>
            <AlertDialogBody>
              Sei sicuro di voler eliminare il rapporto N. {report.reportNumberFormatted}? L'operazione non è reversibile.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={deleteCancelRef} onClick={onDeleteClose}>Annulla</Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3} isLoading={isDeleting}>Elimina</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
